"use strict";
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (Object.hasOwnProperty.call(mod, k)) result[k] = mod[k];
    result["default"] = mod;
    return result;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const React = __importStar(require("react"));
const useUniqueId_1 = require("../useUniqueId");
const Icon_1 = require("../Icon");
const Tabs_styled_1 = require("./Tabs.styled");
const Spinner_1 = __importDefault(require("../Spinner/Spinner"));
const StatusIcon = ({ status }) => (React.createElement("div", { style: { marginLeft: "auto" } },
    status === "loading" && React.createElement(Spinner_1.default, { right: true, color: "primary", size: 14 }),
    status === "success" && React.createElement(Icon_1.YesIcon, { right: true, color: "success", size: 14 }),
    status === "error" && React.createElement(Icon_1.WarningIcon, { right: true, color: "error", size: 14 })));
const Tabs = ({ tabs, scroll, active, onClose, onActivate, onInsert, label, style, id, tabSize = "flex", children, }) => {
    if (!Number.isInteger(active) || active < 0 || active >= tabs.length) {
        active = active > 0 ? tabs.length - 1 : 0;
        if (process.env.NODE_ENV !== "production")
            console.warn("Active tab is out of bounds, falling back to closest value.");
    }
    const uid = useUniqueId_1.useUniqueId(id);
    // track if action was triggered by user or not
    // we need this to activate focus in case action was triggered by user, but not if it was re-render
    const userAction = React.useRef(false);
    const $activeTab = React.useRef(null);
    React.useEffect(() => {
        if ($activeTab.current && userAction.current) {
            $activeTab.current.focus();
            userAction.current = false;
        }
    }, [active, tabs]);
    const onKeyDown = React.useCallback((event) => {
        switch (event.key) {
            case "ArrowRight":
                userAction.current = true;
                event.preventDefault();
                if (active + 1 >= tabs.length) {
                    onActivate(0);
                }
                else {
                    onActivate(active + 1);
                }
                break;
            case "ArrowLeft":
                userAction.current = true;
                event.preventDefault();
                if (active - 1 < 0) {
                    onActivate(tabs.length - 1);
                }
                else {
                    onActivate(active - 1);
                }
                break;
            case "Home":
                userAction.current = true;
                event.preventDefault();
                onActivate(0);
                break;
            case "End":
                userAction.current = true;
                event.preventDefault();
                onActivate(tabs.length - 1);
                break;
            case "Delete":
                userAction.current = true;
                event.preventDefault();
                if (onClose) {
                    onClose(active);
                }
                break;
            case "Enter":
                userAction.current = true;
                // There is no insert on some modern keyboards.
                // You can use `fn` + `enter` to get it on Mac keyboard, it will be reported as Enter.
                // Can be differentiated from enter using this check `e.nativeEvent.code === "NumpadEnter"`
                // WAI ARIA specification doesn't provide recommendations for this case,
                // so let's use Enter `¯\_(ツ)_/¯`
                event.preventDefault();
                if (onInsert) {
                    onInsert(active);
                }
                break;
        }
    }, [active, onActivate, onInsert]);
    const $tabList = React.useRef(null);
    const $tabScroll = React.useRef(null);
    const [leftDisabled, setLeftDisabled] = React.useState(true);
    const [rightDisabled, setRightDisabled] = React.useState(true);
    const scrollLeft = React.useCallback(event => {
        event && event.preventDefault(); // so the button won't get focus when clicked
        if ($tabList.current) {
            $tabList.current.scrollLeft = $tabList.current.scrollLeft - 100;
        }
    }, []);
    const scrollRight = React.useCallback(event => {
        event && event.preventDefault(); // so the button won't get focus when clicked
        if ($tabList.current) {
            $tabList.current.scrollLeft = $tabList.current.scrollLeft + 100;
        }
    }, []);
    const onScroll = React.useCallback(() => {
        if ($tabList.current) {
            const tabListElement = $tabList.current;
            const scrollLeft = tabListElement.scrollLeft;
            setLeftDisabled(scrollLeft === 0);
            setRightDisabled(scrollLeft === tabListElement.scrollWidth - tabListElement.offsetWidth);
        }
    }, []);
    React.useEffect(() => {
        window.addEventListener("resize", onScroll);
        return () => window.removeEventListener("resize", onScroll);
    }, []);
    React.useLayoutEffect(onScroll, []);
    return (React.createElement(Tabs_styled_1.Container, { "data-cy": "operational-ui__Tabs", style: style },
        React.createElement(Tabs_styled_1.PlusWrapper, null,
            React.createElement(Tabs_styled_1.TabList, { scroll: Boolean(scroll), "aria-label": label, onKeyDown: onKeyDown, ref: $tabList, onScroll: onScroll },
                React.createElement(Tabs_styled_1.TabScroll, { ref: $tabScroll }, tabs.map(({ title, icon, color, status }, i) => {
                    const onClick = () => {
                        onActivate(i);
                    };
                    return (React.createElement(Tabs_styled_1.TabHeader, { size: tabSize, center: !scroll, tabIndex: i === active ? 0 : -1, "aria-selected": i === active, "aria-controls": `TabPanel-${uid}-${i}`, id: `TabHeader-${uid}-${i}`, key: i, onClick: onClick, onMouseDown: e => {
                            e.preventDefault();
                            if (e.button === 1 /* middle click */ && onClose) {
                                onClose(i);
                            }
                        }, ref: i === active ? $activeTab : undefined, color: color, last: i === tabs.length - 1 },
                        React.createElement(Tabs_styled_1.TitleIconWrapper, null,
                            icon && React.createElement(Tabs_styled_1.TabIcon, null, icon),
                            React.createElement(Tabs_styled_1.TitleWrapper, { title: title }, title),
                            status && React.createElement(StatusIcon, { status: status })),
                        onClose && (React.createElement(Tabs_styled_1.IconButton, { onClick: e => {
                                e.stopPropagation();
                                onClose(i);
                            } },
                            React.createElement(Icon_1.NoIcon, { size: 9 })))));
                }))),
            onInsert && (React.createElement(Tabs_styled_1.TabButton, { isPlusButton: true, onMouseDown: e => {
                    userAction.current = true;
                    e.preventDefault();
                    onInsert(tabs.length - 1);
                } },
                React.createElement(Tabs_styled_1.IconButton, null,
                    React.createElement(Icon_1.PlusIcon, { size: 14, color: "primary" }))))),
        scroll && (React.createElement(Tabs_styled_1.ScrollButtons, null,
            React.createElement(Tabs_styled_1.TabButton, { onMouseDown: scrollLeft, disabled: leftDisabled },
                React.createElement(Tabs_styled_1.IconButton, null,
                    React.createElement(Icon_1.ChevronLeftIcon, { size: 14, color: leftDisabled ? "color.text.disabled" : "primary" }))),
            React.createElement(Tabs_styled_1.TabButton, { onMouseDown: scrollRight, disabled: rightDisabled },
                React.createElement(Tabs_styled_1.IconButton, null,
                    React.createElement(Icon_1.ChevronRightIcon, { size: 14, color: rightDisabled ? "color.text.disabled" : "primary" }))))),
        React.createElement(Tabs_styled_1.TabContainer, null, tabs.map(({ color }, i) => (React.createElement(Tabs_styled_1.TabPanel, { hidden: i !== active, id: `TabPanel-${uid}-${i}`, "aria-labelledby": `TabHeader-${uid}-${i}`, key: i, color: color }, i === active && children))))));
};
Tabs.defaultProps = {
    scroll: true,
};
exports.default = Tabs;
//# sourceMappingURL=Tabs.js.map